<template>
  <section class="fts-settings-card" v-if="loaded">
    <div>
      <h2>Billing contacts</h2>
      <p class="fts-settings-card-sub">
        Who gets emails about payments, AI credits and limits — people in this workspace or not.
        With no one here, the workspace admins get them.
      </p>
    </div>

    <!-- Said plainly while it is true, so the list never implies mail that is not sent. -->
    <p class="fts-settings-note" v-if="!alertsEnabled">
      Nothing is emailed yet: billing alerts are not switched on here.
    </p>

    <ul class="fts-settings-contacts" v-if="contacts.length">
      <li v-for="email in contacts" :key="email">
        <span>{{ email }}</span>
        <button
          type="button"
          class="fts-settings-btn"
          :disabled="saving"
          :aria-label="`Remove ${email}`"
          @click="remove(email)"
        >
          Remove
        </button>
      </li>
    </ul>

    <form class="fts-settings-contact-add" v-if="contacts.length < max" @submit.prevent="add">
      <input
        v-model.trim="draft"
        type="email"
        aria-label="Add a billing contact"
        placeholder="name@company.com"
        :disabled="saving"
      />
      <button type="submit" class="fts-settings-btn" :disabled="saving || !draft">Add</button>
    </form>
    <p class="fts-settings-note" v-else>That is the most a workspace keeps ({{ max }}).</p>

    <p class="fts-settings-note is-error" role="alert" v-if="error">{{ error }}</p>
  </section>
</template>

<script>
/**
 * Settings › Billing & invoices › Billing contacts (proposal §3 D-6, §4 #6).
 *
 * Who gets the workspace's billing emails. The list saves on every add and remove (D-1:
 * controls save on change), and the server refuses rather than trims — an entry that is not an
 * address comes back named, because a contact silently dropped is a warning nobody receives.
 *
 * The emails themselves are switched on separately (Billing.AlertEmailsEnabled) once their
 * wording is agreed; until then the card says nothing is sent. An API older than this card has
 * no contacts endpoint, and the card simply stays away.
 */
import { commonStore } from "../../store/commonStore";

const ADDRESS = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

export default {
  name: "SettingsBillingContacts",
  props: {
    workspaceId: [String, Number],
  },
  data() {
    return {
      loaded: false,
      contacts: [],
      max: 10,
      alertsEnabled: false,
      draft: "",
      saving: false,
      error: "",
    };
  },
  methods: {
    apply(data) {
      this.contacts = (data && data.contacts) || [];
      if (data && data.max) this.max = data.max;
      this.alertsEnabled = !!(data && data.alertsEnabled);
    },
    async load() {
      try {
        const res = await commonStore.state.client.get("/api/billing/contacts", {
          params: { workspaceId: this.workspaceId },
        });
        this.apply(res.data);
        this.loaded = true;
      } catch (err) {
        this.loaded = false;
      }
    },
    add() {
      const email = this.draft.trim().toLowerCase();
      if (!ADDRESS.test(email)) {
        this.error = "That does not look like an email address.";
        return;
      }
      if (this.contacts.indexOf(email) >= 0) {
        this.error = "";
        this.draft = "";
        return;
      }
      this.save(this.contacts.concat([email]), () => { this.draft = ""; });
    },
    remove(email) {
      this.save(this.contacts.filter(x => x !== email));
    },
    async save(list, done) {
      this.saving = true;
      this.error = "";
      try {
        const res = await commonStore.state.client.post("/api/billing/contacts", {
          workspaceId: this.workspaceId,
          contacts: list,
        });
        this.apply(res.data);
        if (done) done();
      } catch (err) {
        const data = err && err.response && err.response.data;
        this.error = (data && data.message) || "Billing contacts could not be saved. Please try again.";
      } finally {
        this.saving = false;
      }
    },
  },
  watch: {
    workspaceId() {
      this.load();
    },
  },
  created() {
    this.load();
  },
};
</script>
